import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Navigation from '../layout/Navigation';
import Footer from '../layout/Footer';
import styles from '../../pages/LegalPages.module.css';

export const LegalSection = ({ id, number, title, children }) => (
  <section id={id} className={styles.section}>
    <div className={styles.sectionHead}>
      <span className={styles.sectionNum} aria-hidden="true">
        {String(number).padStart(2, '0')}
      </span>
      <h2 className={styles.sectionTitle}>{title}</h2>
    </div>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const LegalPageLayout = ({
  badge,
  title,
  highlight,
  subtitle,
  navItems,
  children,
}) => {
  const [activeId, setActiveId] = useState(navItems[0]?.id ?? '');

  useEffect(() => {
    const sections = navItems
      .map((item) => document.getElementById(item.id))
      .filter(Boolean);

    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) {
          setActiveId(visible.target.id);
        }
      },
      { rootMargin: '-20% 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [navItems]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: 'smooth' });
    setActiveId(id);
  };

  return (
    <>
      <Navigation />
      <main className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroInner}>
              <span className={styles.badge}>{badge}</span>
              <h1 className={styles.title}>
                {title}
                {highlight ? <span className={styles.titleAccent}> {highlight}</span> : null}
              </h1>
              <p className={styles.subtitle}>{subtitle}</p>
            </div>
          </div>
        </section>

        <section className={styles.content}>
          <div className={styles.container}>
            <div className={styles.layout}>
              <aside className={styles.sidebar}>
                <p className={styles.sidebarLabel}>On this page</p>
                <ul className={styles.navList}>
                  {navItems.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={styles.navBtn}
                        data-active={activeId === item.id}
                        onClick={() => scrollToSection(item.id)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>

                <div className={styles.sidebarCta}>
                  <p>Need help with an order?</p>
                  <Link to="/contact" className={styles.sidebarCtaLink}>
                    Contact us
                    <ArrowUpRight size={15} strokeWidth={2.5} aria-hidden="true" />
                  </Link>
                </div>
              </aside>

              <article className={styles.panel}>{children}</article>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default LegalPageLayout;
